import clsx from 'clsx';
import type { LucideIcon } from 'lucide-react';
import { type DragEvent, useId, useState } from 'react';
import { Icon } from '../display/Icon.tsx';
import styles from './FileDrop.module.css';

export interface FileDropProps {
  /** Main line, e.g. "Choose a video file". */
  readonly label: string;
  /** Accepted formats or size limit, one short line. */
  readonly hint?: string | undefined;
  readonly icon: LucideIcon;
  /** `accept` attribute of the file input (extensions or MIME types). */
  readonly accept?: string | undefined;
  readonly file: File | null;
  /** Describes the chosen file, e.g. name and size. */
  readonly describeFile: (file: File) => string;
  readonly onFile: (file: File) => void;
  readonly error?: string | null | undefined;
  readonly disabled?: boolean | undefined;
}

/** Drop zone and file picker in one: click or drag a file onto it. */
export const FileDrop = ({
  label,
  hint,
  icon,
  accept,
  file,
  describeFile,
  onFile,
  error,
  disabled,
}: FileDropProps) => {
  const inputId = useId();
  const [dragging, setDragging] = useState(false);

  const onDrop = (event: DragEvent<HTMLLabelElement>) => {
    event.preventDefault();
    setDragging(false);
    const dropped = event.dataTransfer.files[0];
    if (dropped && !disabled) onFile(dropped);
  };

  return (
    <div className={styles.wrapper}>
      <label
        htmlFor={inputId}
        className={clsx(
          styles.zone,
          dragging && styles.dragging,
          error && styles.invalid,
          disabled && styles.disabled,
        )}
        onDragOver={(event) => {
          event.preventDefault();
          setDragging(true);
        }}
        onDragLeave={() => setDragging(false)}
        onDrop={onDrop}
      >
        <Icon icon={icon} size="lg" className={styles.icon} />
        <span className={styles.label}>{file ? describeFile(file) : label}</span>
        {hint && <span className={styles.hint}>{hint}</span>}
      </label>
      <input
        id={inputId}
        type="file"
        className={styles.input}
        accept={accept}
        disabled={disabled}
        aria-invalid={error ? true : undefined}
        onChange={(event) => {
          const picked = event.currentTarget.files?.[0];
          if (picked) onFile(picked);
          // Allow picking the same file again after a failed attempt.
          event.currentTarget.value = '';
        }}
      />
      {error && (
        <p className={styles.error} role="alert">
          {error}
        </p>
      )}
    </div>
  );
};
